import { defaults, devDefaults } from './defaults'
import type { AppConfig } from './types'

export type { AppConfig }

declare global {
  interface Window {
    WF_CONFIG?: Partial<AppConfig>
    __WF_ENV_CONFIG__?: Partial<AppConfig>
  }
}

// See src/config/README.md for the full config architecture, merge precedence,
// and rules for adding new values.
//
// During the AKS → SWA migration, both config sources coexist.
// When __WF_ENV_CONFIG__ (SWA) is present, WF_CONFIG (AKS) is ignored.

/**
 * Merge order (last wins):
 *   1. Static defaults (same across all environments)
 *   2. Runtime env config from /api/config (SWA) — or dev defaults locally
 *   3. Legacy config.js (AKS) — only when SWA env config is absent
 */
// The Auth0Domain Key Vault secret is the issuer URL (https://.../) the backend
// validates against, but the Auth0 SPA SDK wants the bare host or it builds redirect
// URLs with a doubled slash. Tolerate either form from any config source.
function normalizeAuth0Domain(domain: string | undefined): string | undefined {
  return domain?.replace(/^https?:\/\//, '').replace(/\/+$/, '')
}

export function buildConfig(
  swaEnvConfig: Partial<AppConfig> | undefined,
  aksConfig: Partial<AppConfig> | undefined,
  localStorageApiUrl: string | null = null,
): AppConfig {
  const runtimeConfig = swaEnvConfig ?? aksConfig ?? devDefaults

  const merged = { ...defaults, ...runtimeConfig }
  const settings: AppConfig = {
    ...merged,
    AUTH0_DOMAIN: normalizeAuth0Domain(merged.AUTH0_DOMAIN),
  }

  if (settings.FRONT_ENV !== 'prod' && settings.FRONT_ENV !== 'beta' && localStorageApiUrl) {
    return { ...settings, API_URL: localStorageApiUrl }
  }

  return settings
}

const localApiUrl = localStorage.getItem('dev:api_url')

// In Vite dev mode, ignore the legacy config.js so devDefaults are used as the
// single source of truth for local development. In production builds (AKS or SWA),
// window.WF_CONFIG is still respected as the AKS fallback.
const aksConfig = import.meta.env.DEV ? undefined : window.WF_CONFIG

export const Config: Readonly<AppConfig> = buildConfig(window.__WF_ENV_CONFIG__, aksConfig, localApiUrl)

if (!Config.API_URL) {
  throw new Error('[Config] API_URL is missing - application cannot start')
}

if (localApiUrl && Config.API_URL === localApiUrl) {
  console.info(`%c[Config] Using dev:api_url from localStorage: ${localApiUrl}`, 'color: fuchsia; font-size: 16px;')
}
